import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { CampaignsState } from './initial';
import FetchCampaigns from '@/connection/campaigns/fetch-campaigns';
import CreateCampaign from '@/connection/campaigns/create-campaign';
import DeleteCampaign from '@/connection/campaigns/delete-campaign';
import { Campaign, CreateCampaignRequest } from '@/types/campaign.types';
import { getStorageArray, setStorageArray } from '@/utils/storage/indexed-db';

const MAX_RECENT_TEMPLATES = 6;

export const reducers = (create: ReducerCreators<CampaignsState>) => ({
  loadCampaigns: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        return await FetchCampaigns();
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.campaigns = action.payload || [];
        state.loadingCampaigns = false;
        state.errorLoadingCampaigns = false;
      },
      pending: (state) => {
        state.loadingCampaigns = true;
        state.errorLoadingCampaigns = false;
      },
      rejected: (state) => {
        state.loadingCampaigns = false;
        state.errorLoadingCampaigns = true;
      },
    }
  ),
  createCampaign: create.asyncThunk(
    async (data: CreateCampaignRequest, { rejectWithValue }) => {
      try {
        return await CreateCampaign(data);
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.campaigns = [action.payload, ...state.campaigns];
        state.creatingCampaign = false;
        state.errorCreatingCampaign = false;
        state.lastCreatedCampaign = action.payload;
      },
      pending: (state) => {
        state.creatingCampaign = true;
        state.errorCreatingCampaign = false;
      },
      rejected: (state) => {
        state.creatingCampaign = false;
        state.errorCreatingCampaign = true;
      },
    }
  ),
  addLocalCampaign: create.reducer((state, action: PayloadAction<Campaign>) => {
    state.campaigns = [action.payload, ...state.campaigns];
    state.lastCreatedCampaign = action.payload;
  }),
  deleteCampaign: create.asyncThunk(
    async (campaignUuid: string, { rejectWithValue }) => {
      try {
        await DeleteCampaign(campaignUuid);
        return campaignUuid;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.campaigns = state.campaigns.filter((c) => c.uuid !== action.payload);
        state.deletingCampaignUuid = null;
        state.errorDeletingCampaign = false;
      },
      pending: (state, action) => {
        state.deletingCampaignUuid = action.meta.arg;
        state.errorDeletingCampaign = false;
      },
      rejected: (state) => {
        state.deletingCampaignUuid = null;
        state.errorDeletingCampaign = true;
      },
    }
  ),
  loadFavoriteTemplates: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        return getStorageArray<string>('campaigns/favorites');
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.favoriteTemplates = action.payload;
      },
    }
  ),
  addFavoriteTemplate: create.asyncThunk(
    async (templateId: string, { rejectWithValue }) => {
      try {
        const existing = await getStorageArray<string>('campaigns/favorites');
        const uniq = Array.from(new Set([...existing, templateId]));
        await setStorageArray('campaigns/favorites', uniq);
        return uniq;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.favoriteTemplates = action.payload;
      },
    }
  ),
  removeFavoriteTemplate: create.asyncThunk(
    async (templateId: string, { rejectWithValue }) => {
      try {
        const existing = await getStorageArray<string>('campaigns/favorites');
        const uniq = new Set(existing);
        uniq.delete(templateId);
        const newArray = Array.from(uniq);
        await setStorageArray('campaigns/favorites', newArray);
        return newArray;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.favoriteTemplates = action.payload;
      },
    }
  ),
  loadRecentTemplates: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        return getStorageArray<string>('campaigns/recent');
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.recentTemplates = action.payload;
      },
    }
  ),
  addRecentTemplate: create.asyncThunk(
    async (templateId: string, { rejectWithValue }) => {
      try {
        const existing = await getStorageArray<string>('campaigns/recent');
        const uniq = Array.from(new Set([...existing, templateId]));

        // clamp recent at MAX_RECENT_TEMPLATES size by removing the first item from the list
        if (uniq.length > MAX_RECENT_TEMPLATES) {
          uniq.shift();
        }

        await setStorageArray('campaigns/recent', uniq);
        return uniq;
      } catch (e) {
        console.error(e);
        return rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.favoriteTemplates = action.payload;
      },
    }
  ),
});
